import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ONE_HOUR } from '@/constants/time';
import { fetchClient } from '@/util/fetchClient';

export const cancerFocusSchema = z.object({
  id: z.number().nullish(),
  value: z.string().nullish(),
  label: z.string().optional(),
  shortLabel: z.string().nullish(),
  synonyms: z.array(z.string()).optional(),
});

export type CancerFocus = z.infer<typeof cancerFocusSchema>;

export type GetCancerTypesV2Options = {
  /** Also return hidden parents of visible types (`visible: false`). */
  includeHiddenParents?: boolean;
  /** Add `postCount`: published library posts for the type, descendants included. */
  includePostCounts?: boolean;
};

export async function getCancerTypesV2(options: GetCancerTypesV2Options = {}) {
  const { includeHiddenParents, includePostCounts } = options;

  const { data } =
    includeHiddenParents || includePostCounts
      ? await fetchClient.GET('/v2/cancer-types', {
          params: {
            query: {
              ...(includeHiddenParents && { includeHiddenParents: 'true' }),
              ...(includePostCounts && { includePostCounts: 'true' }),
            },
          },
        })
      : await fetchClient.GET('/v2/cancer-types');

  const cancerTypes = data?.data || [];

  const cancerTypeLookup = new Map(
    cancerTypes.map((item) => [
      item.id,
      {
        label: item.label,
        shortLabel: item.shortLabel ?? null,
        value: item.value,
        synonyms: item.synonyms ?? [],
      },
    ])
  );

  return { cancerTypes, cancerTypeLookup };
}

export const getCancerTypesV2QueryOptions = () =>
  queryOptions({
    queryKey: ['cancerTypesV2'],
    queryFn: () => getCancerTypesV2(),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });

/**
 * The hierarchy-aware list for the Inquiry Library picker: hidden parents included and post
 * counts attached. Its own key, so the Directory's plain list is never replaced by this one.
 */
export const getCancerTypesGroupedQueryOptions = () =>
  queryOptions({
    queryKey: ['cancerTypesV2', 'grouped'],
    queryFn: () => getCancerTypesV2({ includeHiddenParents: true, includePostCounts: true }),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
